import {
  SWARA_NAMES,
  makeRng,
  ragaQuestion,
  ragaTargetCents,
  swaraQuestion,
} from '@swarasense/shared';
import type {
  RagaPhraseQuestion,
  RagaRecord,
  SwaraQuestion,
  SwaraSymbol,
} from '@swarasense/shared';
import { allRagaRecords, searchRagas } from '../api/seed.js';
import type { TonePlayer } from '../audio/tone.js';
import { celebrate } from './celebrate.js';
import {
  MIN_KNOWN_RAGAS,
  RAGA_PRESETS,
  loadKnownRagaNames,
  resolveKnownRagas,
  saveKnownRagaNames,
  starterSet,
} from './ear-known-ragas.js';
import { getGrownupPref, setGrownupPref } from './grownup-pref.js';

/**
 * Ear-training mode (ear-training plan): the learner picks one of TWO
 * exercises at entry —
 *  - "Which swara?" (Task 3): a round of `swaraQuestion`s (T1,
 *    `@swarasense/shared`) for the CURRENT raga.
 *  - "Which raga?" (Task 4): a round of `ragaQuestion`s (T1) — a short
 *    clean-tone phrase drawn from a raga's own arohana+avarohana, guessed
 *    from a multiple-choice list of raga NAMES.
 * Both are played through the tone player (T2) over the tanpura drone —
 * "listen, then tap what you heard". This module owns the whole round's
 * lifecycle (intro -> exercise pick -> question -> question -> ... ->
 * summary) inside one mounted container; `app.ts` only needs to call
 * `renderEarTraining` once per entry and provide the handful of audio/state
 * seams it can't reach on its own (the tone player, the CURRENT Sa in Hz,
 * making sure the tanpura is actually running, and how to leave the mode).
 *
 * Philosophy mirrors `results-panel.ts`/`practice.ts`: the default (child)
 * view is warm and NUMBER-FREE (no %, no cents, no raw score) — a round's
 * outcome is one encouraging sentence. The exact "N of M by ear" count only
 * appears under the same persisted Grown-up disclosure (`grownup-pref.ts`)
 * those two surfaces already use, so a grown-up's opt-in carries here too.
 * Both exercises share this exact scaffolding (intro/progress-dots/summary/
 * Grown-up section) rather than each rolling their own, so a second exercise
 * reads as part of one family, not a bolted-on feature.
 */
export interface EarTrainingDeps {
  /** The raga the swara-ID exercise draws from, and the raga-recognition
   * exercise's guaranteed-included/first target — resolved by the caller
   * (`ragaSeedFor`) from whatever ragam is CURRENT when the round starts. */
  raga: RagaRecord;
  /** Shared with the rest of the app (created once in `app.ts`) so replay
   * taps and the "stop before every new play" rule live in one place. */
  tonePlayer: TonePlayer;
  /** Reads the CURRENT Sa in Hz at call time (`saHzFor(store.get())`) — a
   * function, not a snapshot, so a Sa change made elsewhere mid-round (via
   * the top bar's Sa popover) is picked up by the very next play/replay. */
  saHz: () => number;
  /** Makes sure the tanpura drone is audible on the current Sa before the
   * round's first tone plays — mirrors the transport's own Drone toggle
   * (`app.ts`'s `startTanpura`) so entering ear training doesn't leave a
   * learner listening to a bare tone with no tonal reference. Called once,
   * from an exercise-pick tap (a real user gesture) — if it rejects (e.g.
   * the browser still hasn't unlocked audio for some other reason), the
   * round proceeds anyway rather than getting stuck; `tonePlayer.playSwara`/
   * `playPhrase` themselves already fail soft the same way. */
  ensureDrone: () => Promise<void>;
  /**
   * OPTIONAL sing-back (Task 5): starts a short mic listen and resolves once
   * it can say whether the sung pitch LANDED on `targetCents` (relative to
   * the CURRENT Sa — same convention as every other cents value in this
   * module), needs another try, or couldn't be checked at all. Wired by
   * `app.ts` to reuse the SAME `AudioEngine` + `gradePitch`/`saHzFor`
   * pipeline a normal free-practice take already grades against — this is a
   * second, much shorter LISTEN on that one pipeline, not a second
   * pitch-detection system of its own.
   *
   * Absent entirely (not just a function that always resolves
   * `'unavailable'`) disables the "sing it" control outright — a hand-built
   * `EarTrainingDeps` (a future unit test) can omit it and never touch
   * audio, exactly like `ensureDrone` already lets a caller opt out of the
   * drone. Purely optional and never required to advance a question — the
   * tap-the-swara flow below doesn't read this value at all.
   */
  listenForSwara?: (targetCents: number) => Promise<SingBackResult>;
  /**
   * Cuts a still-listening `listenForSwara` take short — called whenever
   * this module is about to redraw or leave the current screen (a new
   * question, "Go again", "Exit ear training") so an in-flight sing-back
   * never holds the mic open a moment longer than its own visible
   * "Listening…" state is on screen. A no-op if nothing is listening;
   * omitted entirely when `listenForSwara` is (there's nothing to cancel).
   *
   * Returns a promise that resolves only once the engine has ACTUALLY
   * stopped (`app.ts`'s `finish()` awaits `engine.stop()` before settling
   * it) — `exit()` below awaits it before calling `onExit()`, so leaving
   * ear-training mode mid-listen can't hand control back to free practice's
   * "Start listening" while the engine still reads `running` from the
   * sing-back it's in the middle of tearing down (that race would let
   * `engine.start()` silently no-op into a phantom, pitch-less recording).
   */
  cancelSingBack?: () => Promise<void>;
  /** Number of questions per round. Defaults to 8. */
  questionCount?: number;
  /** Leaves ear-training mode entirely, back to free practice — wired by
   * `app.ts` to the same `exitEarMode` its own "Exit ear training"/"Done"
   * affordances would otherwise duplicate. Called from every screen's exit
   * path (the question view's "Exit ear training", and the summary's
   * "Done") so there is exactly one way out, always reachable. */
  onExit: () => void;
  /** Phase A: fired once each time a round's summary is shown — i.e. a round
   * completed. `app.ts` uses it to append a lighter practice-history session
   * (mode 'ear': just the date + current raga, no per-swara metrics). Fires
   * on every completion, including rounds started via the summary's "Go
   * again", so each finished round is counted exactly once. */
  onRoundComplete?: (exercise: ExerciseKind) => void;
}

/** Which of the two ear-training exercises the learner picked at the intro
 * screen. Lives only in this module's own closure state — mirrors how the
 * per-round question arrays below aren't mirrored into the shared store
 * either (see `store.ts`: `practiceMode: 'ear'` is the only signal `app.ts`
 * needs). */
type ExerciseKind = 'swara' | 'raga';

/**
 * Outcome of an OPTIONAL "sing it" mic take (Task 5, sing-back on the
 * swara-ID exercise): whether the sung pitch LANDED on the question's
 * `targetCents`, needs another try, or couldn't be checked at all (mic
 * denied/unsupported, or a take already running elsewhere in the app).
 * `'unavailable'` is a first-class outcome, not an error the caller throws —
 * `app.ts`'s `listenForSwara` resolves to it rather than rejecting, so a
 * denied mic degrades this module straight to its plain "try again, listen
 * once more" copy instead of needing a `.catch`.
 */
export type SingBackResult = 'landed' | 'not-yet' | 'unavailable';

/** Default round length — long enough to sample a handful of swaras/ragas at
 * a rising difficulty without overstaying a child's attention span. */
const DEFAULT_QUESTION_COUNT = 8;

/** How long the "✨ Lovely — that's X!" confirmation sits on screen before
 * auto-advancing to the next question — long enough to read, short enough
 * that a fast learner isn't kept waiting. Matches the ballpark of
 * `celebrate.ts`'s own burst lifetime (~1050ms) so the sparkle and the
 * advance land together. */
const CORRECT_ADVANCE_DELAY_MS = 1100;

/** Share (0-100) of questions answered correctly on the FIRST tap (no wrong
 * attempt first) at/above which the round summary reads as "you matched most
 * of them by ear" rather than "keep listening, it'll click" — every question
 * is eventually answered correctly (there's no "give up and move on"), so
 * first-try accuracy, not completion, is the only meaningful signal for
 * "matched it by ear" vs. "needed a hint". Same leniency rationale as
 * `practice.ts`'s `DRILL_CELEBRATE_PCT`: a small run (8 questions) swings
 * hard on a single miss, so this sits below the free-practice session-wide
 * 75% bar. */
const EAR_CELEBRATE_PCT = 60;

function swaraName(sym: string): string {
  return SWARA_NAMES[sym as SwaraSymbol] ?? sym;
}

/** Swara symbols and raga names come from our own seed data (never user
 * input), so plain interpolation into innerHTML is fine — same trust
 * boundary the rest of the app uses for this data. Escaped anyway as cheap
 * insurance, matching `results-panel.ts`/`practice.ts`. */
function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

/** Rising difficulty across a round: the first third of questions draw from
 * `swaraQuestion`'s level-1 pool (anchor swaras only), the middle third from
 * level 2, the final third from level 3 (the full ladder) — see
 * `questions.ts`'s `poolForLevel` for what each level actually widens to. A
 * 1-question round (edge case) stays at level 1 rather than dividing by
 * zero. */
function levelForIndex(index: number, total: number): number {
  if (total <= 1) return 1;
  const t = index / (total - 1);
  if (t < 1 / 3) return 1;
  if (t < 2 / 3) return 2;
  return 3;
}

/**
 * Builds one round's worth of swara-ID questions, deterministic for a given
 * `seed` (supplied by the caller — see `renderEarTraining`'s own doc for why
 * that seed comes from the web layer, e.g. `Date.now()`, rather than
 * `makeRng`/`swaraQuestion` themselves, which stay pure). Each question gets
 * its own sub-seed (`seed + index * 97`) so questions don't all collapse to
 * the same draw; if a question would repeat the immediately-previous
 * target, a few alternate sub-seeds are tried first (`+ attempt * 13`) so a
 * child doesn't hear "Ga, Ga, Ga" in a row purely by seed coincidence —
 * capped at a handful of attempts so a raga with a very small swara pool
 * (where some repetition is unavoidable) can't loop forever.
 */
function buildSwaraRound(raga: RagaRecord, seed: number, questionCount: number): SwaraQuestion[] {
  const questions: SwaraQuestion[] = [];
  for (let i = 0; i < questionCount; i++) {
    const level = levelForIndex(i, questionCount);
    let question = swaraQuestion(raga, level, makeRng(seed + i * 97));
    const previous = questions[questions.length - 1];
    let attempt = 1;
    while (previous && question.targetSwara === previous.targetSwara && attempt < 6) {
      question = swaraQuestion(raga, level, makeRng(seed + i * 97 + attempt * 13));
      attempt++;
    }
    questions.push(question);
  }
  return questions;
}

/** Picks a random target raga from `pool` (falling back to `currentRaga`
 * when the pool is empty — shouldn't happen once the seed bundle has
 * loaded, but keeps this pure function crash-free either way). */
function pickRagaTarget(
  pool: RagaRecord[],
  currentRaga: RagaRecord,
  rng: () => number,
): RagaRecord {
  if (pool.length === 0) return currentRaga;
  return pool[Math.floor(rng() * pool.length)]!;
}

/**
 * Builds one round's worth of raga-recognition questions (Task 4):
 * `questionCount` calls to `ragaQuestion(target, pool, rng)` (T1,
 * `@swarasense/shared`), deterministic for a given `seed` — same seeding
 * convention as `buildSwaraRound` above (a fresh sub-seed per question,
 * `seed + index * 97`, plus a few alternate attempts on an immediate-repeat
 * target so a round doesn't ask "name this raga" twice in a row for the
 * same answer purely by coincidence).
 *
 * The FIRST question always targets `currentRaga` (the ragam already loaded
 * on the stage — a familiar anchor to start the round on); every other
 * question's target is drawn at random from `pool` (which naturally
 * includes `currentRaga` too, so it can recur later in the round as well) —
 * "keep it varied + include the current raga", per the ear-training plan's
 * Task 4 brief.
 */
export function buildRagaRound(
  currentRaga: RagaRecord,
  pool: RagaRecord[],
  seed: number,
  questionCount: number,
): RagaPhraseQuestion[] {
  const effectivePool = pool.length > 0 ? pool : [currentRaga];
  const questions: RagaPhraseQuestion[] = [];
  let previousTargetName: string | undefined;
  for (let i = 0; i < questionCount; i++) {
    let question: RagaPhraseQuestion;
    let attempt = 0;
    do {
      const subSeed = seed + i * 97 + attempt * 13;
      // Q1 opens on the current practice raga ONLY when it's actually a member
      // of the pool (the user's known-ragas set) — otherwise every target,
      // including Q1's, is drawn from the pool. Without this guard, a round
      // started with a saved set that doesn't contain today's current raga
      // (e.g. after switching practice ragas across sessions) would present +
      // answer a raga the learner never selected — breaking the exercise's
      // "only ragas you know" promise.
      const startWithCurrent =
        i === 0 &&
        attempt === 0 &&
        effectivePool.some((r) => r.name === currentRaga.name);
      const target = startWithCurrent
        ? currentRaga
        : pickRagaTarget(effectivePool, currentRaga, makeRng(subSeed));
      question = ragaQuestion(target, effectivePool, makeRng(subSeed + 1));
      attempt++;
    } while (
      previousTargetName &&
      question.targetRagaName === previousTargetName &&
      attempt < 6 &&
      effectivePool.length > 1
    );
    previousTargetName = question.targetRagaName;
    questions.push(question);
  }
  return questions;
}

/**
 * A gentle, DIRECTIONAL hint after a wrong tap — never "wrong", always
 * framed relative to something the learner already has a name for. Compares
 * the tapped swara's cents (from the same `ragaTargetCents` map
 * `swaraQuestion` itself builds its ladder from, so this always agrees with
 * `question.hintNeighbours`) against the target's: if the tap landed BELOW
 * the target, the target is framed as "a little higher than" its ladder
 * neighbour just below it (falling back to the tapped swara itself at the
 * scale's bottom edge, where there is no lower neighbour); symmetrically for
 * a tap that landed above. Only meaningful for the swara-ID exercise — the
 * raga-recognition exercise has no equivalent "direction" to point in (see
 * `RAGA_WRONG_HINT` below), so it stays with the swara-only path.
 */
function directionalHint(
  question: SwaraQuestion,
  targetCentsBySwara: Record<string, number>,
  tappedSwara: string,
): string {
  const tappedCents = targetCentsBySwara[tappedSwara];
  if (tappedCents === undefined) return 'Listen again and try once more.';
  if (tappedCents < question.targetCents) {
    const reference = question.hintNeighbours.lower ?? tappedSwara;
    return `A little higher than ${swaraName(reference)}.`;
  }
  if (tappedCents > question.targetCents) {
    const reference = question.hintNeighbours.higher ?? tappedSwara;
    return `A little lower than ${swaraName(reference)}.`;
  }
  return 'So close — listen again.'; // two swaras sharing a pitch: rare, but never crash on it
}

/** Gentle wrong-tap copy for the raga-recognition exercise. Unlike the
 * swara-ID exercise, there's no honest "higher/lower" direction to point a
 * learner in for a whole raga's identity (that's not a single scalar the
 * way a swara's cents-from-Sa is) — rather than fabricate a "distinguishing
 * feature" that may or may not actually be the thing that gave it away,
 * this stays a plain, kind nudge back to listening. */
const RAGA_WRONG_HINT = 'Not quite — listen again.';

/** The honest caveat shown throughout the raga-recognition exercise: these
 * phrases are clean tones (a raga's own arohana+avarohana, nothing more),
 * not full gamaka-inflected phrases — real raga identity leans heavily on
 * gamakas (the ornaments/oscillations between swaras), which this
 * approximation doesn't render yet. Kept brief and matter-of-fact, not
 * apologetic or alarming — it's a "here's what this is" note, not a warning. */
const RAGA_CAVEAT =
  "Heads up: these are clean, ornament-free phrases. A raga's real character leans on gamakas (the swara-to-swara ornaments) too — those are coming in a future update.";

/**
 * Mounts the ear-training flow into `container` (replacing its contents) and
 * runs it start to finish: an intro screen with an exercise picker
 * ("Which swara?" / "Which raga?"), `questionCount` questions for whichever
 * was picked, then a warm summary with "Go again"/"Done". Call this once per
 * entry into ear-training mode (`app.ts`'s `enterEarMode`) — a fresh call
 * always starts at the intro/picker screen, mirroring how
 * `renderResultsPanel`/`renderPracticeReview` are each a full, self-
 * contained replace of their container rather than something that needs an
 * unmount step.
 *
 * `active` guards every async continuation (a replay's `await`, and the
 * correct-answer auto-advance timer) against firing after the learner has
 * already exited mid-flight — e.g. tapping "Exit ear training" during the
 * ~1.1s post-correct-answer pause must not have that pause's timer resurrect
 * the (now torn-down) question view a moment later.
 */
export function renderEarTraining(container: HTMLElement, deps: EarTrainingDeps): void {
  const { raga, tonePlayer, saHz, ensureDrone, onExit, listenForSwara, cancelSingBack } = deps;
  const questionCount = deps.questionCount ?? DEFAULT_QUESTION_COUNT;
  const targetCentsBySwara = ragaTargetCents(raga);

  let active = true;
  let exercise: ExerciseKind = 'swara';
  let swaraQuestions: SwaraQuestion[] = [];
  let ragaQuestions: RagaPhraseQuestion[] = [];
  let index = 0;
  let firstTryCorrectCount = 0;
  let missedFirstTryThisQuestion = false;
  /**
   * The learner's "known ragas" selection for the "Which raga?" round — the
   * NAMES only (mirrors `ear-known-ragas.ts`'s persisted shape), lazily
   * initialised the first time the picker panel is opened (either from the
   * intro's "🎶 Which raga?" tap or a later "Edit ragas" reopen) via
   * `getKnownRagaNames()` below, and kept in sync with `localStorage`
   * (`saveKnownRagaNames`) on every add/remove/preset tap so a reload — or a
   * fresh `renderEarTraining` call later in the same session — picks up
   * exactly what was last chosen. `null` means "not loaded/initialised yet"
   * (distinct from `[]`, an explicitly emptied set), so `getKnownRagaNames`
   * only runs its first-run `loadKnownRagaNames()`/`starterSet` fallback
   * once per mount rather than re-triggering it every render.
   */
  let knownRagaNames: string[] | null = null;

  function currentTotal(): number {
    return exercise === 'swara' ? swaraQuestions.length : ragaQuestions.length;
  }

  function exit(): void {
    active = false;
    // Same "sync function, async IIFE inside" shape as `app.ts`'s
    // `exitPracticeMode` — `onExit()` must not fire until any cancelled
    // sing-back has ACTUALLY stopped the engine (awaited, not
    // fire-and-forget): `onExit()` restores free practice's "Start
    // listening" button, and a cancelled-but-still-tearing-down sing-back
    // would otherwise leave `engine.running` reading true for a moment
    // after this returns, letting a fast re-tap of "Start listening"
    // silently no-op into a phantom, pitch-less recording. See
    // `cancelSingBack`'s doc above.
    void (async () => {
      await cancelSingBack?.();
      tonePlayer.stop();
      onExit();
    })();
  }

  async function playCurrentTarget(): Promise<void> {
    // Carry-forward from T2 (tone player): always stop before a new play so
    // a fast double-tap (replay while the previous tone is still ringing)
    // never layers two tones instead of cleanly restarting one.
    tonePlayer.stop();
    if (exercise === 'swara') {
      const question = swaraQuestions[index];
      if (!question) return;
      await tonePlayer.playSwara(saHz(), question.targetCents);
    } else {
      const question = ragaQuestions[index];
      if (!question) return;
      await tonePlayer.playPhrase(
        saHz(),
        question.phrase.map((n) => ({ cents: n.cents })),
      );
    }
  }

  function startSwaraRound(): void {
    exercise = 'swara';
    swaraQuestions = buildSwaraRound(raga, Date.now(), questionCount);
    index = 0;
    firstTryCorrectCount = 0;
    missedFirstTryThisQuestion = false;
    renderQuestion();
    void playCurrentTarget();
  }

  /**
   * The current known-ragas selection, initialising it on first use: the
   * remembered `localStorage` set if there is one, else the first-run
   * `starterSet` (current raga + a few common ragas) — which is
   * immediately persisted too, so a learner who starts a round straight
   * from the starter default without touching the picker still has that
   * exact set remembered next time (matches Task 1's model contract: the
   * starter default is a real, saved choice, not a throwaway placeholder).
   */
  function getKnownRagaNames(): string[] {
    if (knownRagaNames === null) {
      const saved = loadKnownRagaNames();
      if (saved.length > 0) {
        knownRagaNames = saved;
      } else {
        knownRagaNames = starterSet(raga.name, allRagaRecords());
        saveKnownRagaNames(knownRagaNames);
      }
    }
    return knownRagaNames;
  }

  /** Replaces the known-ragas selection, persists it, and re-renders the
   * picker panel (preserving whatever the learner had typed into search, so
   * tapping "+ Kalyani" doesn't clear an in-progress search for "kal..."). */
  function mutateKnownRagas(mutate: (current: Set<string>) => void): void {
    const searchInput = container.querySelector<HTMLInputElement>('#known-raga-search-input');
    const preserveQuery = searchInput?.value ?? '';
    const next = new Set(getKnownRagaNames());
    mutate(next);
    knownRagaNames = [...next];
    saveKnownRagaNames(knownRagaNames);
    renderKnownRagasPanel(preserveQuery);
  }

  /**
   * Builds and starts a "Which raga?" round scoped to the learner's known-
   * ragas selection (Task 2) — `pool` is the resolved subset, NOT
   * `allRagaRecords()`, so the target rotates across only the ragas the
   * learner picked and every distractor option comes from that same set.
   * Reuses the exact `buildRagaRound` question logic (unchanged) that
   * previously drew from the full 91-raga bundle.
   */
  function startRagaRoundFromKnownSet(): void {
    exercise = 'raga';
    const pool = resolveKnownRagas(getKnownRagaNames());
    ragaQuestions = buildRagaRound(raga, pool, Date.now(), questionCount);
    index = 0;
    firstTryCorrectCount = 0;
    missedFirstTryThisQuestion = false;
    renderQuestion();
    void playCurrentTarget();
  }

  function startCurrentExercise(): void {
    if (exercise === 'swara') startSwaraRound();
    else startRagaRoundFromKnownSet();
  }

  /**
   * The intro's "🎵 Which swara?" pick: unlock the drone (best-effort — a
   * rejection here still lets the round proceed, same fail-soft posture
   * `tonePlayer.playSwara`/`playPhrase` already have), then start the round.
   * (Task 2: "🎶 Which raga?" no longer goes through this — it opens the
   * known-ragas picker instead, whose own "Start round" button does the
   * equivalent unlock-then-`ensureDrone`-then-start sequence; `kind` stays
   * generic here rather than narrowing to `'swara'` only because that's the
   * one real caller today, not a structural requirement.)
   */
  function pickExercise(kind: ExerciseKind): void {
    // Unlock the tone player's AudioContext FIRST, synchronously, before any
    // `await` below — this is the actual user tap (real user activation).
    // `await ensureDrone()` a line down is itself already several
    // microtasks removed from the tap; on stricter mobile browsers (iOS
    // Safari) an AudioContext created/resumed only after that point stays
    // `suspended`, so the drone plays but the quiz tone never does. See
    // `TonePlayer.unlock`'s doc in `tone.ts` for the full story.
    tonePlayer.unlock();
    void (async () => {
      try {
        await ensureDrone();
      } catch {
        // No drone, no problem — the tone itself is still the point; degrade
        // gracefully rather than blocking the round on the tanpura.
      }
      if (!active) return;
      exercise = kind;
      startCurrentExercise();
    })();
  }

  function renderIntro(): void {
    container.innerHTML = `
      <div class="reveal-note">— ear training —</div>
      <div class="card ear-card child-summary">
        <p class="child-headline">Ready to test your ear?</p>
        <p class="ear-copy">Pick a game, then listen closely and tap what you heard. Take your time, and listen again as often as you like.</p>
        <div class="ear-exercise-picker" role="group" aria-label="Choose an ear-training exercise">
          <div class="ear-exercise-option">
            <button class="btn primary ear-exercise-btn" id="ear-pick-swara" type="button">
              🎵 Which swara?
            </button>
          </div>
          <div class="ear-exercise-option">
            <button class="btn primary ear-exercise-btn" id="ear-pick-raga" type="button">
              🎶 Which raga?
            </button>
            <p class="ear-caveat">${escapeHtml(RAGA_CAVEAT)}</p>
          </div>
        </div>
      </div>
      <div class="ear-exit-row">
        <button class="btn" id="ear-exit-btn" type="button">Exit ear training</button>
      </div>`;

    container
      .querySelector<HTMLButtonElement>('#ear-pick-swara')
      ?.addEventListener('click', () => pickExercise('swara'));
    // Task 2: "Which raga?" no longer jumps straight into a round drawn from
    // all 91 ragas — it opens the "Which ragas do you know?" picker first
    // (see `renderKnownRagasPanel` below), and the round only starts once
    // the learner taps "Start round" there. No audio plays on this tap
    // itself, so (unlike `pickExercise`) there's nothing to `tonePlayer
    // .unlock()` synchronously here — the picker's own "Start round" tap is
    // the real audio-triggering gesture and unlocks there instead.
    container
      .querySelector<HTMLButtonElement>('#ear-pick-raga')
      ?.addEventListener('click', () => renderKnownRagasPanel());
    container.querySelector<HTMLButtonElement>('#ear-exit-btn')?.addEventListener('click', exit);
  }

  /**
   * The "Which ragas do you know?" selection panel (Task 2) — shown BEFORE
   * a "Which raga?" round starts (from the intro's tap, or an "Edit ragas"
   * reopen from the round header/summary): the current known-ragas set as
   * removable chips, the `RAGA_PRESETS` quick-add buttons, a search box to
   * add specific ragas, and a friendly count + "Start round" (disabled,
   * with a gentle hint, below `MIN_KNOWN_RAGAS`).
   *
   * `searchQuery` re-populates the search box + its results after a
   * mutation (`mutateKnownRagas` above) so adding a raga from a search
   * doesn't wipe out what the learner was still typing.
   */
  function renderKnownRagasPanel(searchQuery = ''): void {
    const selected = resolveKnownRagas(getKnownRagaNames());
    const selectedNames = new Set(selected.map((r) => r.name));
    const count = selected.length;
    const playable = count >= MIN_KNOWN_RAGAS;

    const chipsHtml =
      selected.length > 0
        ? selected
            .map(
              (r) =>
                `<button class="btn ear-known-chip" type="button" data-remove="${escapeHtml(r.name)}" aria-label="Remove ${escapeHtml(r.name)} from known ragas">${escapeHtml(r.name)} <span aria-hidden="true">✕</span></button>`,
            )
            .join('')
        : `<p class="ear-copy">No ragas picked yet — search below or tap a preset.</p>`;

    const presetsHtml = RAGA_PRESETS.map(
      (preset) =>
        `<button class="btn ear-known-preset-btn" type="button" data-preset="${escapeHtml(preset.id)}">${escapeHtml(preset.label)}</button>`,
    ).join('');

    const resultsHtml = knownRagaSearchResultsHtml(searchQuery, selectedNames);

    const hintHtml = playable
      ? ''
      : `<p class="ear-hint">Pick a few more — at least ${MIN_KNOWN_RAGAS}.</p>`;

    container.innerHTML = `
      <div class="reveal-note">— ear training · which raga? —</div>
      <div class="card ear-card ear-known-panel child-summary">
        <p class="child-headline">Which ragas do you know?</p>
        <p class="ear-copy">Pick the ragas you'd like the game to ask about — search for one, or start with a preset. You can change this anytime.</p>
        <div class="ear-known-chips" id="known-raga-chips" role="group" aria-label="Ragas you know">${chipsHtml}</div>
        <div class="ear-known-presets" role="group" aria-label="Quick preset sets">${presetsHtml}</div>
        <div class="ear-known-search">
          <label class="sr-only" for="known-raga-search-input">Search ragas to add</label>
          <input
            type="text"
            id="known-raga-search-input"
            class="ear-known-search-input"
            placeholder="Search ragas…"
            autocomplete="off"
            value="${escapeHtml(searchQuery)}"
          />
          <div class="ear-known-search-results" id="known-raga-search-results" role="group" aria-label="Add a raga">${resultsHtml}</div>
        </div>
        <div class="ear-known-footer" aria-live="polite">
          <p class="ear-known-count" id="known-raga-count">${count} raga${count === 1 ? '' : 's'}</p>
          ${hintHtml}
          <button class="btn primary" id="known-raga-start-btn" type="button"${playable ? '' : ' disabled'}>Start round</button>
        </div>
      </div>
      <div class="ear-known-back-row">
        <button class="btn" id="known-raga-back-btn" type="button">‹ Back</button>
      </div>
      <div class="ear-exit-row">
        <button class="btn" id="ear-exit-btn" type="button">Exit ear training</button>
      </div>`;

    for (const btn of container.querySelectorAll<HTMLButtonElement>('.ear-known-chip')) {
      btn.addEventListener('click', () => {
        const name = btn.dataset.remove!;
        mutateKnownRagas((current) => current.delete(name));
      });
    }
    for (const btn of container.querySelectorAll<HTMLButtonElement>('.ear-known-preset-btn')) {
      btn.addEventListener('click', () => {
        const preset = RAGA_PRESETS.find((p) => p.id === btn.dataset.preset);
        if (!preset) return;
        const names = preset.ragaNames(allRagaRecords());
        mutateKnownRagas((current) => {
          for (const name of names) current.add(name);
        });
      });
    }
    wireKnownRagaAddButtons();

    const searchInput = container.querySelector<HTMLInputElement>('#known-raga-search-input')!;
    // Only the results list is re-rendered on every keystroke (not the whole
    // panel) — a full re-render would blur the input and lose the cursor
    // position mid-type. Add/remove/preset taps below DO trigger a full
    // re-render (via `mutateKnownRagas`), but those are discrete clicks, not
    // continuous typing, so losing/restoring focus there is fine.
    searchInput.addEventListener('input', () => {
      const resultsEl = container.querySelector<HTMLElement>('#known-raga-search-results')!;
      const currentlySelected = new Set(resolveKnownRagas(getKnownRagaNames()).map((r) => r.name));
      resultsEl.innerHTML = knownRagaSearchResultsHtml(searchInput.value, currentlySelected);
      wireKnownRagaAddButtons();
    });
    if (searchQuery.trim() !== '') {
      searchInput.focus();
      searchInput.setSelectionRange(searchInput.value.length, searchInput.value.length);
    }

    container
      .querySelector<HTMLButtonElement>('#known-raga-start-btn')
      ?.addEventListener('click', () => {
        if (!playable) return;
        // Same synchronous-unlock-before-await rationale as `pickExercise`
        // above — "Start round" is the actual audio-triggering gesture for
        // this flow (the picker itself is silent), so the unlock happens
        // here, not on the earlier "Which raga?"/preset/search taps.
        tonePlayer.unlock();
        void (async () => {
          try {
            await ensureDrone();
          } catch {
            // No drone, no problem — degrade gracefully, same as `pickExercise`.
          }
          if (!active) return;
          startRagaRoundFromKnownSet();
        })();
      });
    container
      .querySelector<HTMLButtonElement>('#known-raga-back-btn')
      ?.addEventListener('click', () => renderIntro());
    container.querySelector<HTMLButtonElement>('#ear-exit-btn')?.addEventListener('click', exit);
  }

  /** Ranked, alias-aware add-candidates for the picker's search box (Task
   * 2): reuses `searchRagas` (which itself reuses `rankRagaMatches`) over a
   * generous limit so filtering OUT already-known ragas afterward doesn't
   * starve the visible list, then caps the DISPLAYED count so the results
   * row stays a manageable tap-friendly size. Empty query shows nothing (a
   * blank "type to search" state) rather than all 91 ragas at once. */
  function knownRagaSearchResultsHtml(query: string, selectedNames: Set<string>): string {
    const trimmed = query.trim();
    if (trimmed === '') return '';
    const matches = searchRagas(trimmed, 50)
      .filter((r) => !selectedNames.has(r.name))
      .slice(0, 8);
    if (matches.length === 0) return `<p class="ear-copy">No matching ragas.</p>`;
    return matches
      .map(
        (r) =>
          `<button class="btn ear-known-add-btn" type="button" data-add="${escapeHtml(r.name)}">+ ${escapeHtml(r.name)}</button>`,
      )
      .join('');
  }

  /** (Re)wires every currently-rendered "+ RagaName" add button — called
   * after both a full panel render and a keystroke-only results refresh,
   * since either can replace the buttons' DOM nodes. */
  function wireKnownRagaAddButtons(): void {
    for (const btn of container.querySelectorAll<HTMLButtonElement>('.ear-known-add-btn')) {
      btn.addEventListener('click', () => {
        const name = btn.dataset.add!;
        mutateKnownRagas((current) => current.add(name));
      });
    }
  }

  /** "Edit ragas" (Task 2): reopens the known-ragas picker from mid-round or
   * the summary, stopping any ringing tone first (a raga phrase could still
   * be playing when the learner taps it from the question header) — shared
   * by `renderRagaQuestion` and `renderSummary` so both wire the exact same
   * behavior rather than each duplicating it. */
  function wireEditRagasButton(): void {
    container
      .querySelector<HTMLButtonElement>('#ear-edit-ragas-btn')
      ?.addEventListener('click', () => {
        tonePlayer.stop();
        void cancelSingBack?.();
        renderKnownRagasPanel();
      });
  }

  function progressDotsHtml(total: number): string {
    return Array.from({ length: total }, (_, i) => {
      const state = i < index ? 'done' : i === index ? 'current' : 'todo';
      return `<span class="ear-dot ${state}"></span>`;
    }).join('');
  }

  /** After a correct tap on either exercise: advance to the next question,
   * or the summary once the round is over. Shared so the two exercises'
   * correct-tap handlers don't each duplicate the same timer/guard. */
  function scheduleAdvance(): void {
    window.setTimeout(() => {
      if (!active) return;
      index++;
      if (index >= currentTotal()) {
        renderSummary();
      } else {
        missedFirstTryThisQuestion = false;
        renderQuestion();
        void playCurrentTarget();
      }
    }, CORRECT_ADVANCE_DELAY_MS);
  }

  function renderQuestion(): void {
    // A fresh question always cuts a still-listening sing-back short first,
    // so it never keeps the mic open a moment past the screen it belongs
    // to. Fire-and-forget here (unlike `exit()`'s awaited call) is fine:
    // nothing reachable from THIS screen re-opens the mic outside
    // `listenForSwara` itself (free practice's "Start listening" stays
    // hidden for the whole ear-training session, only reappearing after
    // `exit()`'s own awaited cancellation), and the redraw below already
    // orphans any DOM a late resolution might otherwise have touched.
    void cancelSingBack?.();
    if (exercise === 'raga') renderRagaQuestion();
    else renderSwaraQuestion();
  }

  function renderSwaraQuestion(): void {
    const question = swaraQuestions[index]!;
    const total = swaraQuestions.length;
    // Progress is a row of dots, not "3 of 8" text — the count itself lives
    // only in this element's `aria-label` (for assistive tech), never as
    // visible digits, matching the child view's number-free rule.
    const dots = progressDotsHtml(total);
    const optionsHtml = question.options
      .map(
        (sym) =>
          `<button class="btn ear-option" type="button" data-swara="${escapeHtml(sym)}" aria-label="${escapeHtml(swaraName(sym))}">${escapeHtml(sym)}</button>`,
      )
      .join('');
    // Task 5: the OPTIONAL "sing it" sing-back — only rendered at all when
    // `app.ts` actually wired a `listenForSwara` (undefined disables it
    // outright, see that dep's doc above). Sits BELOW the tap options, after
    // (never instead of, never blocking) the primary tap-the-swara flow —
    // a second, independent way to engage with the SAME question, not a
    // required step in front of it. `data-sing-state` drives the small
    // idle/listening/landed/not-yet/unavailable style hooks in styles.css;
    // the actual copy always lives in `#ear-sing-feedback`, never as a class
    // name alone, so screen readers get the same words a sighted learner does.
    const singHtml = listenForSwara
      ? `<div class="ear-sing-row" id="ear-sing-row" data-sing-state="idle">
          <button class="btn" id="ear-sing-btn" type="button" aria-label="Sing the note you heard">🎤 Sing it</button>
          <p class="ear-sing-feedback" id="ear-sing-feedback" aria-live="polite"></p>
        </div>`
      : '';

    container.innerHTML = `
      <div class="reveal-note">— ear training · ${escapeHtml(raga.name)} —</div>
      <div class="card ear-card ear-question child-summary" id="ear-question-card" data-state="answering">
        <div class="ear-progress" role="img" aria-label="Question ${index + 1} of ${total}">${dots}</div>
        <p class="child-headline" id="ear-headline" aria-live="polite">Which swara do you hear?</p>
        <div class="ear-listen-row">
          <button class="btn primary" id="ear-replay-btn" type="button">↻ Listen again</button>
        </div>
        <p class="ear-hint" id="ear-hint" aria-live="polite"></p>
        <div class="ear-options" role="group" aria-label="Choose the swara you heard">${optionsHtml}</div>
        ${singHtml}
      </div>
      <div class="ear-exit-row">
        <button class="btn" id="ear-exit-btn" type="button">Exit ear training</button>
      </div>`;

    const cardEl = container.querySelector<HTMLElement>('#ear-question-card')!;
    const headlineEl = container.querySelector<HTMLElement>('#ear-headline')!;
    const hintEl = container.querySelector<HTMLElement>('#ear-hint')!;
    const optionButtons = [...container.querySelectorAll<HTMLButtonElement>('.ear-option')];

    container.querySelector<HTMLButtonElement>('#ear-replay-btn')?.addEventListener('click', () => {
      // Same synchronous-unlock-before-await rationale as `pickExercise`
      // above — a replay tap is its own fresh user gesture, and
      // `playCurrentTarget` awaits inside `tonePlayer.playSwara`/`playPhrase`.
      tonePlayer.unlock();
      void playCurrentTarget();
    });
    container.querySelector<HTMLButtonElement>('#ear-exit-btn')?.addEventListener('click', exit);

    if (listenForSwara) {
      const singBtn = container.querySelector<HTMLButtonElement>('#ear-sing-btn')!;
      const singRow = container.querySelector<HTMLElement>('#ear-sing-row')!;
      const singFeedback = container.querySelector<HTMLElement>('#ear-sing-feedback')!;
      // Guards a late resolution from an EARLIER tap on this same question
      // (the child taps "Sing it" twice in a row) against clobbering a
      // NEWER attempt's in-progress "Listening…" state. A late resolution
      // from a PREVIOUS question, if any, needs no such guard — this
      // render's `container.innerHTML` replace has already orphaned that
      // question's DOM nodes, so a stale `.then()` writing to them is
      // inert; `renderQuestion`'s `cancelSingBack?.()` additionally tears
      // the engine down proactively so that stale listen doesn't linger.
      let singBackToken = 0;
      singBtn.addEventListener('click', () => {
        const token = ++singBackToken;
        singRow.dataset.singState = 'listening';
        singBtn.disabled = true;
        singBtn.textContent = '🎤 Listening…';
        singFeedback.textContent = 'Listening…';
        void listenForSwara(question.targetCents).then((result) => {
          if (!active || token !== singBackToken) return; // moved on — nothing left to update
          singBtn.disabled = false;
          singBtn.textContent = result === 'landed' ? '🎤 Sing it again' : '🎤 Sing it';
          singRow.dataset.singState = result;
          if (result === 'landed') {
            // Number-free by construction (see the module doc): a sparkle
            // and one warm line, exactly like a correct tap's own ✨
            // moment — never a band name, cents, or percent.
            singFeedback.textContent = "✨ That's it!";
            celebrate(singRow, { particles: 5 });
          } else if (result === 'not-yet') {
            singFeedback.textContent = 'Try again — listen once more.';
          } else {
            // Mic denied/unsupported, or a take already running elsewhere —
            // gentle and non-alarming; the tap-the-swara flow above never
            // needed this to work anyway.
            singFeedback.textContent = "Can't listen right now — tapping still works!";
          }
        });
      });
    }

    for (const btn of optionButtons) {
      btn.addEventListener('click', () => {
        if (cardEl.dataset.state === 'correct') return; // already advancing — ignore a stray extra tap
        const tapped = btn.dataset.swara!;
        if (tapped === question.targetSwara) {
          cardEl.dataset.state = 'correct';
          if (!missedFirstTryThisQuestion) firstTryCorrectCount++;
          hintEl.textContent = '';
          headlineEl.textContent = `✨ Lovely — that's ${swaraName(tapped)}!`;
          celebrate(cardEl);
          for (const b of optionButtons) b.disabled = true;
          scheduleAdvance();
        } else {
          missedFirstTryThisQuestion = true;
          cardEl.dataset.state = 'wrong';
          hintEl.textContent = directionalHint(question, targetCentsBySwara, tapped);
          void playCurrentTarget();
        }
      });
    }
  }

  /**
   * The raga-recognition question screen (Task 4): plays `question.phrase`
   * (the target raga's own arohana+avarohana in clean tones) over the
   * drone, then offers `question.options` — raga NAMES (target +
   * distractors, straight from `ragaQuestion` — real ragas only, never
   * fabricated) — as tap targets. Correct/wrong feedback and the
   * progress-dots/exit-row scaffolding mirror `renderSwaraQuestion` above;
   * the differences are the headline, the "↻ Replay phrase" wording (a
   * PHRASE, not a single tone), the options being full names rather than
   * single swara symbols (`.ear-option--raga`, styled for longer text in
   * `styles.css`), a plain non-directional wrong-tap hint (see
   * `RAGA_WRONG_HINT`'s doc above for why), and the always-visible honest
   * caveat about clean-tone phrases vs. gamakas.
   */
  function renderRagaQuestion(): void {
    const question = ragaQuestions[index]!;
    const total = ragaQuestions.length;
    const dots = progressDotsHtml(total);
    const optionsHtml = question.options
      .map(
        (name) =>
          `<button class="btn ear-option ear-option--raga" type="button" data-raga="${escapeHtml(name)}">${escapeHtml(name)}</button>`,
      )
      .join('');

    container.innerHTML = `
      <div class="reveal-note">— ear training · which raga? —</div>
      <div class="card ear-card ear-question child-summary" id="ear-question-card" data-state="answering">
        <div class="ear-progress" role="img" aria-label="Question ${index + 1} of ${total}">${dots}</div>
        <p class="child-headline" id="ear-headline" aria-live="polite">Which raga do you hear?</p>
        <div class="ear-listen-row">
          <button class="btn primary" id="ear-replay-btn" type="button">↻ Replay phrase</button>
        </div>
        <p class="ear-hint" id="ear-hint" aria-live="polite"></p>
        <div class="ear-options" role="group" aria-label="Choose the raga you heard">${optionsHtml}</div>
        <p class="ear-caveat">${escapeHtml(RAGA_CAVEAT)}</p>
      </div>
      <div class="ear-exit-row">
        <button class="btn" id="ear-edit-ragas-btn" type="button">Edit ragas</button>
        <button class="btn" id="ear-exit-btn" type="button">Exit ear training</button>
      </div>`;

    const cardEl = container.querySelector<HTMLElement>('#ear-question-card')!;
    const headlineEl = container.querySelector<HTMLElement>('#ear-headline')!;
    const hintEl = container.querySelector<HTMLElement>('#ear-hint')!;
    const optionButtons = [...container.querySelectorAll<HTMLButtonElement>('.ear-option--raga')];

    container.querySelector<HTMLButtonElement>('#ear-replay-btn')?.addEventListener('click', () => {
      // Same synchronous-unlock-before-await rationale as `pickExercise`
      // above — a replay tap is its own fresh user gesture, and
      // `playCurrentTarget` awaits inside `tonePlayer.playSwara`/`playPhrase`.
      tonePlayer.unlock();
      void playCurrentTarget();
    });
    container.querySelector<HTMLButtonElement>('#ear-exit-btn')?.addEventListener('click', exit);
    wireEditRagasButton();

    for (const btn of optionButtons) {
      btn.addEventListener('click', () => {
        if (cardEl.dataset.state === 'correct') return; // already advancing — ignore a stray extra tap
        const tapped = btn.dataset.raga!;
        if (tapped === question.targetRagaName) {
          cardEl.dataset.state = 'correct';
          if (!missedFirstTryThisQuestion) firstTryCorrectCount++;
          hintEl.textContent = '';
          headlineEl.textContent = `✨ Lovely — that's ${tapped}!`;
          celebrate(cardEl);
          for (const b of optionButtons) b.disabled = true;
          scheduleAdvance();
        } else {
          missedFirstTryThisQuestion = true;
          cardEl.dataset.state = 'wrong';
          hintEl.textContent = RAGA_WRONG_HINT;
          void playCurrentTarget();
        }
      });
    }
  }

  function renderSummary(): void {
    // Phase A: a round just finished — let the host record it (once per
    // completion; "Go again" starts a fresh round that ends in its own
    // `renderSummary` call, so this fires per finished round, not per tap).
    deps.onRoundComplete?.(exercise);
    const total = currentTotal();
    const firstTryPct = total > 0 ? (100 * firstTryCorrectCount) / total : 0;
    const wentWell = firstTryPct >= EAR_CELEBRATE_PCT;
    // Number-free by construction: three warm buckets, chosen from
    // `firstTryCorrectCount`/`wentWell` but never printing either value —
    // the exact count only ever appears in the Grown-up section below.
    const headline =
      firstTryCorrectCount === 0
        ? 'Good try — want to go again?'
        : wentWell
          ? 'You matched most of them by ear! ✨'
          : "Nice listening — keep going and it'll click!";
    const revealNote =
      exercise === 'raga'
        ? '— ear training · which raga? —'
        : `— ear training · ${escapeHtml(raga.name)} —`;
    // Task 2: "Edit ragas" only makes sense after a "Which raga?" round —
    // swara-ID's summary is untouched (still just "Go again"/"Done").
    const editRagasBtnHtml =
      exercise === 'raga'
        ? `<button class="btn" id="ear-edit-ragas-btn" type="button">Edit ragas</button>`
        : '';

    container.innerHTML = `
      <div class="reveal-note">${revealNote}</div>
      <div class="card ear-card ear-summary child-summary">
        <p class="child-headline">${escapeHtml(headline)}</p>
        <div class="ear-actions">
          ${editRagasBtnHtml}
          <button class="btn" id="ear-again-btn" type="button">Go again</button>
          <button class="btn primary" id="ear-done-btn" type="button">Done</button>
        </div>
      </div>
      <div class="grownup-toggle-row">
        <button
          type="button"
          class="grownup-toggle"
          id="ear-grownup-toggle"
          aria-expanded="false"
          aria-controls="ear-grownup-section"
        >
          Grown-up view <span class="chev" aria-hidden="true">▾</span>
        </button>
      </div>
      <div class="grownup-section" id="ear-grownup-section" hidden>
        <div class="metrics">
          <div class="metric">
            <div class="k">By ear</div>
            <div class="val">${firstTryCorrectCount} of ${total}</div>
          </div>
        </div>
      </div>`;

    if (wentWell) {
      const anchor = container.querySelector<HTMLElement>('.ear-summary');
      if (anchor) celebrate(anchor);
    }

    const grownupToggle = container.querySelector<HTMLButtonElement>('#ear-grownup-toggle')!;
    const grownupSection = container.querySelector<HTMLElement>('#ear-grownup-section')!;
    function setGrownupExpanded(expanded: boolean): void {
      grownupToggle.setAttribute('aria-expanded', String(expanded));
      grownupSection.hidden = !expanded;
      grownupToggle.classList.toggle('open', expanded);
    }
    setGrownupExpanded(getGrownupPref());
    grownupToggle.addEventListener('click', () => {
      const next = grownupToggle.getAttribute('aria-expanded') !== 'true';
      setGrownupExpanded(next);
      setGrownupPref(next);
    });

    container.querySelector<HTMLButtonElement>('#ear-again-btn')?.addEventListener('click', () => {
      // Same synchronous-unlock-before-await rationale as `pickExercise` —
      // "Go again" is its own fresh gesture, and `startCurrentExercise`
      // fires off `playCurrentTarget()` (which awaits) for the round's
      // first question.
      tonePlayer.unlock();
      startCurrentExercise();
    });
    container.querySelector<HTMLButtonElement>('#ear-done-btn')?.addEventListener('click', exit);
    if (exercise === 'raga') wireEditRagasButton();
  }

  renderIntro();
}
